import jsPDF from 'jspdf'
import autoTable from 'jspdf-autotable'

export interface PdfSection {
  heading: string
  rows: Array<[string, string]>
}

export interface PdfTable {
  heading: string
  head: string[]
  body: Array<Array<string | number>>
}

interface PdfExportOptions {
  title: string
  subtitle?: string
  sections: PdfSection[]
  table?: PdfTable
  fileName: string
}

const BRAND_NAME = 'Mint Calculator'

/**
 * Generates a branded PDF report for a given calculator's results and
 * triggers a browser download. Used for both the Budget and Loan calculators.
 */
export function exportResultsToPdf({ title, subtitle, sections, table, fileName }: PdfExportOptions) {
  const doc = new jsPDF({ unit: 'pt', format: 'a4' })
  const pageWidth = doc.internal.pageSize.getWidth()
  const marginX = 40
  let cursorY = 50

  doc.setFillColor(15, 23, 42)
  doc.rect(0, 0, pageWidth, 84, 'F')
  doc.setTextColor(16, 185, 129)
  doc.setFont('helvetica', 'bold')
  doc.setFontSize(18)
  doc.text(BRAND_NAME, marginX, 36)
  doc.setTextColor(226, 232, 240)
  doc.setFont('helvetica', 'normal')
  doc.setFontSize(12)
  doc.text(title, marginX, 58)
  if (subtitle) {
    doc.setFontSize(9)
    doc.setTextColor(148, 163, 184)
    doc.text(subtitle, marginX, 72)
  }

  cursorY = 104
  doc.setTextColor(15, 23, 42)

  sections.forEach((section) => {
    doc.setFont('helvetica', 'bold')
    doc.setFontSize(11)
    doc.setTextColor(20, 184, 166)
    doc.text(section.heading, marginX, cursorY)
    cursorY += 8

    autoTable(doc, {
      startY: cursorY,
      margin: { left: marginX, right: marginX },
      theme: 'plain',
      styles: { fontSize: 9, textColor: [30, 41, 59], cellPadding: 4 },
      columnStyles: { 0: { fontStyle: 'normal' }, 1: { fontStyle: 'bold', halign: 'right' } },
      body: section.rows,
    })

    cursorY = (doc as unknown as { lastAutoTable: { finalY: number } }).lastAutoTable.finalY + 18
  })

  if (table) {
    doc.setFont('helvetica', 'bold')
    doc.setFontSize(11)
    doc.setTextColor(20, 184, 166)
    doc.text(table.heading, marginX, cursorY)
    cursorY += 8

    autoTable(doc, {
      startY: cursorY,
      margin: { left: marginX, right: marginX },
      styles: { fontSize: 8, cellPadding: 3 },
      headStyles: { fillColor: [15, 23, 42], textColor: [16, 185, 129] },
      head: [table.head],
      body: table.body,
    })
  }

  const pageCount = doc.internal.getNumberOfPages()
  for (let i = 1; i <= pageCount; i++) {
    doc.setPage(i)
    doc.setFontSize(8)
    doc.setTextColor(148, 163, 184)
    doc.text(
      `${BRAND_NAME} - www.mintcalculator.com - Generated ${new Date().toLocaleDateString()}`,
      marginX,
      doc.internal.pageSize.getHeight() - 20,
    )
  }

  doc.save(fileName)
}
